import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Progress,
  Row,
  Spinner,
  Table,
} from 'reactstrap';

import BaseGrid from '../Common/BaseGrid';
import { get } from '../../helpers/api_helper';

function formatDateForInput(date) {
  return date.toISOString().slice(0, 10);
}

function buildDefaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);

  return {
    startDate: formatDateForInput(start),
    endDate: formatDateForInput(end),
  };
}

function formatPercent(value) {
  return `${(Number(value || 0) * 100).toFixed(1)}%`;
}

function ReturnReasonPerformance({ t }) {
  const defaults = buildDefaultRange();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: defaults.startDate,
    endDate: defaults.endDate,
    email: '',
    returnReasons: [],
  });
  const [gridMode, setGridMode] = useState('listings');
  const [summary, setSummary] = useState({
    total_returns: 0,
    distinct_reason_count: 0,
    return_reasons: [],
    available_return_reasons: [],
  });
  const [appliedGridFilters, setAppliedGridFilters] = useState('');
  const [gridRefreshKey, setGridRefreshKey] = useState(0);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const query = new URLSearchParams();

      if (filters.startDate) {
        query.set('start_date', filters.startDate);
      }

      if (filters.endDate) {
        query.set('end_date', filters.endDate);
      }

      if (filters.email.trim() !== '') {
        query.set('email', filters.email.trim());
      }

      filters.returnReasons.forEach((reason) => {
        query.append('return_reasons', reason);
      });

      query.set('grid_mode', gridMode);

      const response = await get(`/api/happyreturns/performance?${query.toString()}`);
      setSummary(response || {
        total_returns: 0,
        distinct_reason_count: 0,
        return_reasons: [],
        available_return_reasons: [],
      });
      setAppliedGridFilters(query.toString());
      setGridRefreshKey((current) => current + 1);
    } catch (loadError) {
      setError(t('Unable to load Happy Returns performance.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);
  const returnReasonOptions = (summary.available_return_reasons || []).map((reason) => ({
    label: `${reason.reason} (${reason.count})`,
    value: reason.reason,
  }));
  const gridColumns = gridMode === 'emails'
    ? {
      email: {
        label: 'Email',
        sort: true,
      },
      sales_count: {
        label: 'Sales',
        sort: true,
      },
      count: {
        label: 'Returned Orders',
        sort: true,
      },
      return_rate: {
        label: 'Return Rate',
        sort: true,
        format: (value) => formatPercent(value),
      },
    }
    : {
      sku: {
        label: 'SKU',
        sort: true,
      },
      title: {
        label: 'Listing',
        sort: true,
        format: (value) => value || '--',
      },
      count: {
        label: 'Returns',
        sort: true,
      },
      percentage: {
        label: 'Share',
        sort: true,
        format: (value) => formatPercent(value),
      },
    };

  return (
    <div className="happy-returns-performance">
      <Card>
        <CardHeader className="bg-transparent border-bottom">
          {t('Return Reason Performance')}
        </CardHeader>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md="3">
              <label htmlFor="happy-returns-start-date" className="form-label">{t('Start Date')}</label>
              <Input
                id="happy-returns-start-date"
                type="date"
                className="happy-returns-performance__date-input"
                value={filters.startDate}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  startDate: event.target.value,
                }))}
              />
            </Col>
            <Col md="3">
              <label htmlFor="happy-returns-end-date" className="form-label">{t('End Date')}</label>
              <Input
                id="happy-returns-end-date"
                type="date"
                className="happy-returns-performance__date-input"
                value={filters.endDate}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  endDate: event.target.value,
                }))}
              />
            </Col>
            <Col md="4">
              <label htmlFor="happy-returns-email" className="form-label">{t('Email Filter')}</label>
              <Input
                id="happy-returns-email"
                type="text"
                placeholder={t('Optional exact email match')}
                value={filters.email}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  email: event.target.value,
                }))}
              />
            </Col>
            <Col md="10">
              <label htmlFor="happy-returns-reasons" className="form-label">{t('Return Reasons')}</label>
              <Select
                inputId="happy-returns-reasons"
                isMulti
                options={returnReasonOptions}
                value={returnReasonOptions.filter((option) => (
                  filters.returnReasons.includes(option.value)
                ))}
                onChange={(selectedOptions) => {
                  setFilters((current) => ({
                    ...current,
                    returnReasons: (selectedOptions || []).map((option) => option.value),
                  }));
                }}
                placeholder={t('All return reasons')}
                classNamePrefix="supplier-select"
              />
            </Col>
            <Col md="2">
              <Button color="primary" onClick={loadSummary} disabled={loading} className="w-100">
                {loading ? (
                  <>
                    <Spinner size="sm" />
                    {' '}
                    {t('Loading...')}
                  </>
                ) : t('Apply')}
              </Button>
            </Col>
          </Row>

          {error && (
            <Alert color="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </CardBody>
      </Card>

      <Row className="mt-1">
        <Col md="4">
          <Card className="mini-stats-wid">
            <CardBody>
              <p className="text-muted fw-medium mb-2">{t('Total Returns')}</p>
              <h3 className="mb-0">{summary.total_returns}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="mini-stats-wid">
            <CardBody>
              <p className="text-muted fw-medium mb-2">{t('Distinct Reasons')}</p>
              <h3 className="mb-0">{summary.distinct_reason_count}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="mini-stats-wid">
            <CardBody>
              <p className="text-muted fw-medium mb-2">{t('Date Range')}</p>
              <h6 className="mb-0">
                {filters.startDate || '--'}
                {' '}
                to
                {' '}
                {filters.endDate || '--'}
              </h6>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card>
        <CardHeader className="bg-transparent border-bottom">
          {t('Return Reason Percentages')}
        </CardHeader>
        <CardBody>
          {loading && (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && summary.return_reasons.length === 0 && (
            <Alert color="info" className="mb-0">
              {t('No return reasons found for the selected filters.')}
            </Alert>
          )}

          {!loading && summary.return_reasons.length > 0 && (
            <Table responsive className="align-middle mb-0">
              <thead>
                <tr>
                  <th>{t('Return Reason')}</th>
                  <th style={{ width: '40%' }}>{t('Share')}</th>
                  <th>{t('Count')}</th>
                  <th>{t('Percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.return_reasons.map((reason) => (
                  <tr key={reason.reason}>
                    <td>{reason.reason}</td>
                    <td>
                      <Progress
                        value={Number(reason.percentage || 0) * 100}
                        color="info"
                        style={{ height: '18px' }}
                      >
                        {formatPercent(reason.percentage)}
                      </Progress>
                    </td>
                    <td>{reason.count}</td>
                    <td>{formatPercent(reason.percentage)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="bg-transparent border-bottom">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <span>
              {gridMode === 'emails' ? t('Highest Offending Emails') : t('Listings With Returns')}
            </span>
            <div style={{ minWidth: '260px' }}>
              <label htmlFor="happy-returns-grid-mode" className="form-label mb-1">{t('Grid View')}</label>
              <Input
                id="happy-returns-grid-mode"
                type="select"
                value={gridMode}
                onChange={(event) => {
                  const nextMode = event.target.value;
                  setGridMode(nextMode);
                  setAppliedGridFilters((current) => {
                    const nextFilters = new URLSearchParams(current);
                    nextFilters.set('grid_mode', nextMode);
                    return nextFilters.toString();
                  });
                  setGridRefreshKey((current) => current + 1);
                }}
              >
                <option value="listings">{t('Listings')}</option>
                <option value="emails">{t('Highest Offending Emails')}</option>
              </Input>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <BaseGrid
            url="/api/happyreturns/performance/listings"
            shouldUpdate={gridRefreshKey}
            filters={appliedGridFilters}
            keyProp="grid_key"
            config={{
              columns: gridColumns,
              pagination: {
                perPage: 10,
                sort: gridMode === 'emails' ? 'return_rate' : 'count',
                direction: 'desc',
              },
            }}
          />
        </CardBody>
      </Card>
    </div>
  );
}

ReturnReasonPerformance.defaultProps = {
  t: (value) => value,
};

ReturnReasonPerformance.propTypes = {
  t: PropTypes.func,
};

export default ReturnReasonPerformance;
